import React from "react";
//#region types
import { DimensionEnums, FormEnums } from "models/enums";
import { GenericFormSection } from "models/propTypes/genericFormProps";
import { AccountingFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { WintSuperkollAPIModelAccounting } from "wint-api-client/dist/types";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
//#region components
import { useTransactionService, useAccountService } from "serviceHooks";
//#endregion
import { FilterHelper, QueryStateHelper } from "utils/helpers";

function useCompanyBooksReportFilterDefinition() {
  const transactionService = useTransactionService();
  const accountService = useAccountService();
  const [series, setSeries] = React.useState<AutocompleteListItem[]>([]);
  const [categories, setCategories] = React.useState<AutocompleteListItem[]>([]);
  const [accounts, setAccounts] = React.useState<AutocompleteListItem[]>([]);

  React.useEffect(() => {
    transactionService.getSeries().then((res) => {
      setSeries(
        res?.map((s) => ({
          id: s.id ?? "",
          value: s.name ?? "",
        })) ?? []
      );
    });

    transactionService.getCategories().then((res) => {
      setCategories(
        res?.map((s) => ({
          id: s.id,
          value: s.name ?? "",
          description: s.accountRangeText,
        })) ?? []
      );
    });

    accountService.getAccounts({} as WintSuperkollAPIModelAccounting.AccountFilter).then((res) => {
      setAccounts(
        res?.items?.map((s) => ({
          id: s.id,
          value: s.number.toString() ?? "",
          description: s.name,
        })) ?? []
      );
    });
  }, []);

  const apiToReportFilterFormatter = async (
    filter: WintSuperkollAPIModelAccounting.TransactionFilter
  ): Promise<AccountingFormTypes.CompanyBooksReportFilter> => {
    const reportFilter: AccountingFormTypes.CompanyBooksReportFilter = {
      series: filter.series?.map((s) => ({ id: s, value: s })) ?? [],
      dimensions: filter.dimensions,
      categoryId: filter.categoryId,
      category: filter.categoryId !== undefined ? categories.find((c) => c.id === filter.categoryId)?.value : undefined,
    };

    if (filter.accounts?.length) {
      const accounts = await accountService
        .getAccounts({
          exactAccounts: filter.accounts,
          active: true,
        } as WintSuperkollAPIModelAccounting.AccountFilter)
        .then((res) => res.items);
      reportFilter.accountNumbers = accounts?.length
        ? accounts.map((account) => ({ id: account.id, value: account.number.toString() ?? "" }))
        : undefined;
    }

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Accounting.CompanyBooksReportFilter.BookingDate,
      filter.bookingDateStart,
      filter.bookingDateEnd
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Accounting.CompanyBooksReportFilter.Account,
      filter.accountStart,
      filter.accountEnd
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Accounting.CompanyBooksReportFilter.Amount,
      filter.minAmount,
      filter.maxAmount
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Accounting.CompanyBooksReportFilter.VoucherNumber,
      filter.voucherNumberStart,
      filter.voucherNumberEnd
    );

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<AccountingFormTypes.CompanyBooksReportFilter>[] = [
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        account: {
          label: TranslationKeys.Common.PostedAs,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
        accountNumbers: {
          label: TranslationKeys.Common.Account,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          extraProps: {
            items: accounts,
          },
        },
        voucherNumber: {
          label: TranslationKeys.Common.Voucher,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
        series: {
          label: TranslationKeys.Accounting.Series,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          extraProps: {
            items: series,
          },
        },
        category: {
          label: TranslationKeys.Common.Categories,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.Accounting.CompanyBooksReportFilter.CategoryId,
            items: categories,
          },
        },
      },
    },
    {
      label: TranslationKeys.Common.Dates,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        bookingDate: {
          label: TranslationKeys.Common.Date,
          type: FormEnums.FormFieldType.DATERANGE,
        },
      },
    },
    {
      label: TranslationKeys.Common.Amount,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        amount: {
          label: TranslationKeys.Common.Amount,
          type: FormEnums.FormFieldType.NUMBERRANGE,
          extraProps: {
            allowNegative: true,
            decimalPoints: 2,
          },
        },
      },
    },
    {
      label: TranslationKeys.Common.Tags,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        dimensions: {
          label: TranslationKeys.Common.Tags,
          type: FormEnums.FormFieldType.DIMENSIONMULTIPLEINPUTSELECTOR,
          extraProps: {
            type: DimensionEnums.DimensionCategory.All,
          },
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<AccountingFormTypes.CompanyBooksReportFilter> = {
    bookingDate: QueryStateHelper.dateRangeQueryEncoder(
      FormFieldConstants.Accounting.CompanyBooksReportFilter.BookingDate
    ),
    account: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Accounting.CompanyBooksReportFilter.Account),
    amount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Accounting.CompanyBooksReportFilter.Amount),
    voucherNumber: QueryStateHelper.numberRangeQueryEncoder(
      FormFieldConstants.Accounting.CompanyBooksReportFilter.VoucherNumber
    ),
    series: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.Accounting.CompanyBooksReportFilter.Series
    ),
    dimensions: QueryStateHelper.dimensionsQueryEncoder(
      FormFieldConstants.Accounting.CompanyBooksReportFilter.Dimensions
    ),
    categoryId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Accounting.CompanyBooksReportFilter.CategoryId),
    category: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Accounting.CompanyBooksReportFilter.Category),
    accountNumbers: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.Accounting.CompanyBooksReportFilter.AccountNumbers
    ),
    isAudit: QueryStateHelper.booleanQueryEncoder(FormFieldConstants.Accounting.CompanyBooksReportFilter.IsAudit),
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useCompanyBooksReportFilterDefinition;
